//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import { yupResolver } from '@hookform/resolvers/yup'
import {
  Box,
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  SelectChangeEvent,
  Typography,
  useTheme,
} from '@mui/material'
import React, { ReactElement, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'

import EditField from '@react/components/elements/EditField'
// import { useAddLiquidityPoolMutation, useUpdateLiquidityPoolMutation } from '@react/redux/restApi/liquidityPoolApi'
import { yupEthAddr } from '@shared/Enums'
import { PerimeterChainAddressesInterface } from '@react/utils/PerimeterAddresses'
import { EthAddr, ETH_ADDR_ZERO, StringTypeEnum, toFieldMetaData } from '@shared/SchemaTypes'
import * as yup from 'yup'
import { InferType } from 'yup'
import { onPromise } from '@react/components/UiHelpers'

import { toErc20Wei, toRoot365 } from '@shared/conversions'

import { useOtChainState, useOtDispatch } from '@react/redux/store'

import { createPoolFlexTx } from '@react/signedTxs/createPoolFlexTx'
import { logOT } from '@shared/loggingShared'
import { AssetImage } from '@react/components/icons/AssetImage'
import { LiquidityAsset } from '@shared/LiquidityAsset'

export const PoolFlexCreateSchema = yup.object({
  tokenSymbol: yup
    .string()
    .required()
    .label('Token Symbol')
    .meta(toFieldMetaData({ propName: 'tokenSymbol' })),
  // PoolDTO
  addr: yupEthAddr
    .label('Pool Contract Address')
    .meta(toFieldMetaData({ propName: 'addr', stringType: StringTypeEnum.STABLE_COINS, offChain: true })),
  poolName: yup
    .string()
    .required()
    .label('Vault Name')
    .meta(toFieldMetaData({ propName: 'poolName' })),

  currency: yup
    .mixed<LiquidityAsset>()
    // .oneOf(Object.values(CurrencyEnum))
    // .default(CurrencyEnum.USD)
    .label('Currency')
    .meta(toFieldMetaData({ propName: 'currency', stringType: StringTypeEnum.STABLE_COIN_DEFINITION })),

  maxCapacity: yup
    .number()
    .label('maxCapacity')
    .default(100000000)
    .meta(
      toFieldMetaData({
        propName: 'maxCapacity',
        stringType: StringTypeEnum.STABLE_COINS,
      })
    ),
  endDate: yup
    .string()
    .label('endDate')
    .default(Math.round(new Date(2050, 1, 1).getTime() / 1000).toString())
    .meta(toFieldMetaData({ propName: 'endDate' })),

  closeOfDepositTimeHour: yup
    .number()
    .required()
    .positive()
    .label('Close of Deposit Business Hour')
    .meta(toFieldMetaData({ propName: 'closeOfDepositTimeHour', stringType: StringTypeEnum.TIME_INTERVAL_HOURS })),
  closeOfWithdrawTimeHour: yup
    .number()
    .required()
    .positive()
    .label('Close of Withdraw Business Hour')
    .meta(toFieldMetaData({ propName: 'closeOfWithdrawTimeHour', stringType: StringTypeEnum.TIME_INTERVAL_HOURS })),

  borrowerAddr: yupEthAddr.label('Borrower Manager Addr').meta(
    toFieldMetaData({
      propName: 'borrowerAddr',
      stringType: StringTypeEnum.ETH_ADDR,
    })
  ),
  borrowerWalletAddr: yupEthAddr.label('Borrower Wallet Addr').meta(
    toFieldMetaData({
      propName: 'borrowerWalletAddr',
      stringType: StringTypeEnum.ETH_ADDR,
    })
  ),
  originationFee: yup
    .number()
    .required()
    .default(100)
    .label('originationFee')
    .meta(toFieldMetaData({ propName: 'originationFee' })),
  dailyOriginationFeeRate: yup
    .number()
    .required()
    .default(100)
    .label('dailyOriginationFeeRate')
    .meta(toFieldMetaData({ propName: 'dailyOriginationFeeRate' })),
  transferInDays: yup
    .number()
    .required()
    .test('Is positive?', 'ERROR: The number must be greater than 0!', (value) => value >= 0)
    .test('<=4>', 'ERROR: The number must be greater than 0!', (value) => value <= 4)
    .label('transferInDays')
    .meta(toFieldMetaData({ propName: 'transferInDays', stringType: StringTypeEnum.TIME_INTERVAL_DAYS })),
  transferOutDays: yup
    .number()
    .required()
    .test('Is positive?', 'ERROR: The number must be greater than 0!', (value) => value >= 0)
    .test('<=4>', 'ERROR: The number must be greater than 0!', (value) => value <= 4)
    .label('transferOutDays')
    .meta(toFieldMetaData({ propName: 'transferOutDays', stringType: StringTypeEnum.TIME_INTERVAL_DAYS })),
})

export interface PoolFlexCreateFormData extends InferType<typeof PoolFlexCreateSchema> {
  tokenSymbol: string
  maxCapacity: number
  endDate: string
  addr: EthAddr
  borrowerAddr: EthAddr
  borrowerWalletAddr: EthAddr
  closeOfDepositTimeHour: number
  closeOfWithdrawTimeHour: number

  originationFee: number
  asset: LiquidityAsset
  dailyOriginationFeeRate: number
  transferInDays: number
  transferOutDays: number
}

export const PoolFlexCreateFormDataDefaultObject = (
  perimeterAddresses: PerimeterChainAddressesInterface
): PoolFlexCreateFormData => {
  return {
    tokenSymbol: '',
    addr: ETH_ADDR_ZERO,
    poolName: '',
    currency: undefined,
    maxCapacity: 100000000,
    endDate: Math.round(new Date(2050, 1, 1).getTime() / 1000).toString(),
    closeOfDepositTimeHour: 18,
    closeOfWithdrawTimeHour: 18,

    borrowerAddr: '',
    borrowerWalletAddr: '',
    originationFee: 100,
    asset: perimeterAddresses.liquidityAssets[0],
    dailyOriginationFeeRate: toRoot365(100),
    transferInDays: 2,
    transferOutDays: 2,
  }
}
export const poolFlexConfigSettingsFormat = (poolFormData: PoolFlexCreateFormData): string[] => {
  return [
    toErc20Wei(poolFormData.maxCapacity),
    poolFormData.borrowerAddr,
    poolFormData.borrowerWalletAddr,
    (poolFormData.closeOfDepositTimeHour * 3600).toString(),
    (poolFormData.closeOfWithdrawTimeHour * 3600).toString(),

    Math.round(poolFormData.originationFee * 10000).toString(),
    toRoot365(Math.round(poolFormData.originationFee * 10000)).toString(),
    poolFormData.transferInDays.toString(),
    poolFormData.transferOutDays.toString(),
  ]
}

export default function PoolFlexCreateForm(): ReactElement {
  const {
    chainConfiguration,
    chainConfiguration: { perimeterAddresses },
  } = useOtChainState()

  const initialValues = perimeterAddresses && PoolFlexCreateFormDataDefaultObject(perimeterAddresses)
  const [isSubmitted, setIsSubmitted] = React.useState(false)
  const chainState = useOtChainState()

  const dispatch = useOtDispatch()

  const SUPPORTED_ASSETS: LiquidityAsset[] =
    (perimeterAddresses?.liquidityAssets.length ?? 0 > 0) ? (perimeterAddresses?.liquidityAssets ?? []) : []

  const [selectedAsset, setSelectedAsset] = useState(SUPPORTED_ASSETS[0])

  const handleAssetChange = (event: SelectChangeEvent) => {
    setSelectedAsset(
      SUPPORTED_ASSETS.find((asset: LiquidityAsset) => asset.symbol === event.target.value) ?? SUPPORTED_ASSETS[0]
    )
  }

  const onSubmit: SubmitHandler<PoolFlexCreateFormData> = (poolFormData: PoolFlexCreateFormData): void => {
    logOT.debug('Submit poolFormData', poolFormData)
    const name = poolFormData.poolName
    const tokenSymbol = poolFormData.tokenSymbol
    const asset = selectedAsset

    setIsSubmitted(true)

    const poolConfigSettingsStruct = {
      maxCapacity: toErc20Wei(poolFormData.maxCapacity),
      borrowerManagerAddr: poolFormData.borrowerAddr,
      borrowerWalletAddr: poolFormData.borrowerWalletAddr,

      closeOfDepositTime: (poolFormData.closeOfDepositTimeHour * 3600).toString(),
      closeOfWithdrawTime: (poolFormData.closeOfWithdrawTimeHour * 3600).toString(),

      originationFee: Math.round(poolFormData.originationFee).toString(), // 0.5%,
      dailyOriginationFeeRate: toRoot365(Math.round(poolFormData.originationFee)).toString(),
      transferInDays: poolFormData.transferInDays.toString(),
      transferOutDays: poolFormData.transferOutDays.toString(),
    }
    const poolConfigSettings = Object.values(poolConfigSettingsStruct) as string[]

    if (chainState.userConnection?.signer) {
      dispatch(createPoolFlexTx({ poolConfigSettings, name, tokenSymbol, liquidityAssetAddress: asset.addr }))
    }
  }

  const theme = useTheme()
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<PoolFlexCreateFormData>({
    defaultValues: initialValues,
    resolver: yupResolver(PoolFlexCreateSchema),
  })

  // const [addLiquidityPool] = useAddLiquidityPoolMutation()

  logOT.debug('errors', errors)
  return (
    <div data-testid="POOL_CREATE_FORM">
      <form onSubmit={onPromise(handleSubmit(onSubmit))}>
        <Paper>
          {isSubmitted ? <div data-testid="IS_SUBMITTED">IS_SUBMITTED</div> : <div></div>}
          <Box px={3} py={2}>
            <Typography variant="bodyBold" align="center" margin="dense">
              Create a new Investment Pool
            </Typography>

            <Grid container spacing={1}>
              <Grid item xs={12} sm={12}>
                <EditField
                  errors={errors}
                  register={register}
                  initialValues={initialValues}
                  fieldSchema={PoolFlexCreateSchema.describe().fields.poolName}
                ></EditField>
              </Grid>
              <Grid item xs={12} sm={4}>
                <EditField
                  errors={errors}
                  register={register}
                  initialValues={initialValues}
                  fieldSchema={PoolFlexCreateSchema.describe().fields.tokenSymbol}
                ></EditField>
              </Grid>
              <Grid item xs={12} sm={4}>
                <EditField
                  errors={errors}
                  register={register}
                  initialValues={initialValues}
                  fieldSchema={PoolFlexCreateSchema.describe().fields.closeOfDepositTimeHour}
                ></EditField>
              </Grid>
              <Grid item xs={12} sm={4}>
                <EditField
                  errors={errors}
                  register={register}
                  initialValues={initialValues}
                  fieldSchema={PoolFlexCreateSchema.describe().fields.closeOfWithdrawTimeHour}
                ></EditField>
              </Grid>

              <Grid item xs={12} sm={4}>
                <EditField
                  errors={errors}
                  register={register}
                  initialValues={initialValues}
                  fieldSchema={PoolFlexCreateSchema.describe().fields.originationFee}
                ></EditField>
              </Grid>
              <Grid item xs={12} sm={4}>
                <EditField
                  errors={errors}
                  register={register}
                  initialValues={initialValues}
                  fieldSchema={PoolFlexCreateSchema.describe().fields.transferInDays}
                ></EditField>
              </Grid>
              <Grid item xs={12} sm={4}>
                <EditField
                  errors={errors}
                  register={register}
                  initialValues={initialValues}
                  fieldSchema={PoolFlexCreateSchema.describe().fields.transferOutDays}
                ></EditField>
              </Grid>
              <Grid item xs={12} sm={4}>
                <EditField
                  errors={errors}
                  register={register}
                  initialValues={initialValues}
                  currency={selectedAsset.symbol}
                  fieldSchema={PoolFlexCreateSchema.describe().fields.maxCapacity}
                ></EditField>
              </Grid>
              <Grid item xs={12} sm={4}>
                <EditField
                  errors={errors}
                  register={register}
                  initialValues={initialValues}
                  fieldSchema={PoolFlexCreateSchema.describe().fields.borrowerAddr}
                ></EditField>
              </Grid>
              <Grid item xs={12} sm={4}>
                <EditField
                  errors={errors}
                  register={register}
                  initialValues={initialValues}
                  fieldSchema={PoolFlexCreateSchema.describe().fields.borrowerWalletAddr}
                ></EditField>
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl sx={{ m: 1, width: 300 }}>
                  <InputLabel id="asset-label">Asset</InputLabel>
                  <Select
                    labelId="asset-label"
                    id="asset-select"
                    input={<OutlinedInput id="asset-select" label="Asset" />}
                    value={selectedAsset.symbol}
                    renderValue={(assetSymbol: string) => {
                      const asset =
                        SUPPORTED_ASSETS.find((asset: LiquidityAsset) => asset.symbol === assetSymbol) ??
                        SUPPORTED_ASSETS[0]
                      return (
                        <div
                          style={{
                            flexDirection: 'row',
                            display: 'flex',
                            justifyContent: 'flex-start',
                            alignItems: 'center',
                          }}
                        >
                          <div style={{ marginTop: '4px' }}>
                            <AssetImage asset={asset.symbol} />
                          </div>
                          <Typography sx={{ marginLeft: 2 }} variant="bodyBold">
                            {asset.symbol}
                          </Typography>
                        </div>
                      )
                    }}
                    onChange={handleAssetChange}
                  >
                    {SUPPORTED_ASSETS.map((asset: LiquidityAsset) => (
                      <MenuItem
                        value={asset.symbol}
                        key={`asset-option-${asset.symbol}`}
                        style={{
                          paddingLeft: 12,
                          display: 'flex',
                          flexDirection: 'row',
                          justifyContent: 'flex-start',
                          alignItems: 'center',
                        }}
                      >
                        <div style={{ marginTop: '4px' }}>
                          <AssetImage asset={asset.symbol} />
                        </div>
                        <Typography sx={{ marginLeft: 2 }} variant="bodyBold">
                          {asset.symbol}
                        </Typography>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={12}>
                {!isSubmitted && !!chainConfiguration && (
                  <Button
                    sx={{
                      backgroundColor: theme.palette.fg.main,
                      color: '#FFFFFF',
                      padding: '11px 16px 11px 16px',
                      borderRadius: '6px',
                    }}
                    type="submit"
                    data-testid={'submit'}
                    variant="contained"
                  >
                    Submit
                  </Button>
                )}
                {!chainConfiguration && (
                  <Typography variant="bodyBold" align="center" margin="dense">
                    Please connect your wallet to submit a pool
                  </Typography>
                )}
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </form>
    </div>
  )
}
